import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { SoccerBall, Globe, CaretDown, Check } from "@phosphor-icons/react";
import { useI18n } from "../contexts/I18nContext";
import { supabase } from "../lib/supabase";
import { useAuth } from "../contexts/AuthContext";
import LanguagePicker from "../components/LanguagePicker";

function GoogleIcon({ size = 26 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#EA4335"
        d="M5.266 9.765A7.077 7.077 0 0 1 12 4.909c1.69 0 3.218.6 4.418 1.582L19.91 3C17.782 1.145 15.055 0 12 0 7.27 0 3.198 2.698 1.24 6.65l4.026 3.115Z"
      />
      <path
        fill="#34A853"
        d="M16.04 18.013c-1.09.703-2.474 1.078-4.04 1.078a7.077 7.077 0 0 1-6.723-4.823l-4.04 3.067A11.965 11.965 0 0 0 12 24c2.933 0 5.735-1.043 7.834-3l-3.793-2.987Z"
      />
      <path
        fill="#4285F4"
        d="M19.834 21c2.195-2.048 3.62-5.096 3.62-9 0-.71-.109-1.473-.272-2.182H12v4.637h6.436c-.317 1.559-1.17 2.766-2.395 3.558L19.834 21Z"
      />
      <path
        fill="#FBBC05"
        d="M5.277 14.268A7.12 7.12 0 0 1 4.909 12c0-.782.125-1.533.357-2.235L1.24 6.65A11.934 11.934 0 0 0 0 12c0 1.92.445 3.73 1.237 5.335l4.04-3.067Z"
      />
    </svg>
  );
}

function YandexIcon({ size = 28, className }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 44 44"
      fill="currentColor"
      fillRule="evenodd"
      className={className}
      aria-hidden="true"
    >
      <rect width="44" height="44" rx="22" fill="#FC3F1D"></rect>
      <path d="M22.5968 24.2728C23.8823 27.0888 24.3109 28.0682 24.3109 31.4505V35.9347H19.7196V28.3743L11.0573 9.53467H15.8476L22.5968 24.2728ZM28.2594 9.53467L22.6427 22.2985H27.3105L32.9425 9.53467H28.2594Z" fill="white"></path>
    </svg>
  );
}



const ICON_BTN =
  "w-16 h-16 rounded-full bg-white flex items-center justify-center shadow-xl hover:bg-neutral-100 active:scale-95 transition-all";

export default function Login() {
  const { t, locale } = useI18n();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const isRuDomain = typeof window !== "undefined" && window.location.hostname.endsWith(".ru");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!authLoading && user) {
      navigate("/app", { replace: true });
    }
  }, [user, authLoading, navigate]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#02371b] text-white flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-white/20 border-t-[#41a42b] rounded-full animate-spin" />
      </div>
    );
  }

  const signInWith = async (provider) => {
    const redirectTo = `${window.location.origin}/auth/callback`;
    await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo },
    });
  };

  const signInWithEmail = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");
    const redirectTo = `${window.location.origin}/auth/callback?locale=${locale}`;
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { 
        emailRedirectTo: redirectTo,
        data: { locale }
      },
    });
    setLoading(false);
    if (error) {
      setMessage(error.message);
    } else {
      setMessage(t("magic_link_sent"));
      setEmail("");
    }
  };

  return (
    <div className="min-h-screen flex items-stretch justify-center bg-[#010f08]">
      <div className="relative w-full max-w-md min-h-screen overflow-hidden flex flex-col">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#011208] via-[#02371b] to-black" />
          <div className="absolute -top-32 -right-24 w-96 h-96 rounded-full bg-[#41a42b]/25 blur-3xl" />
          <div className="absolute bottom-0 left-0 w-72 h-72 rounded-full bg-[#0c5e2e]/20 blur-3xl" />
        </div>

        <header className="relative z-20 flex items-center justify-between px-5 pt-6">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center overflow-hidden">
              <img src="/logo.png" alt="TippTop" className="w-full h-full object-cover" />
            </div>
            <span className="font-display text-white text-lg tracking-tight">TippTop</span>
          </div>

          <LanguagePicker variant="dark" />
        </header>

        <div className="relative z-10 flex-1 flex flex-col justify-center px-6 pb-6 pt-12">
          <div className="text-[11px] font-bold tracking-[0.3em] uppercase text-[#41a42b] mb-4">{t("fifa_world_cup")}</div>
          <h1 className="font-display text-white text-5xl sm:text-6xl leading-[0.95]">
            <span className="text-[#41a42b]">{t("login_hero_line1")}</span><br />
            {t("login_hero_line2")}
          </h1>
          <p className="text-white/70 text-sm sm:text-base mt-5 leading-relaxed max-sm:text-xs">{t("login_hero_sub")}</p>

          <form onSubmit={signInWithEmail} className="mt-6">
            <div className="relative">
              <input
                type="email"
                placeholder={t("email_placeholder")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full h-12 px-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-[#41a42b] transition-colors text-sm"
                required
              />
              <button
                type="submit"
                disabled={loading}
                className="absolute right-2 top-2 h-8 px-4 bg-[#41a42b] text-white text-xs font-bold uppercase rounded-lg hover:bg-[#348e22] disabled:opacity-50 transition-colors"
              >
                {loading ? "..." : t("send_link")}
              </button>
            </div>
            {message && <p className="text-white/50 text-xs mt-2 text-center">{message}</p>}
          </form>

          <div className="mt-6">
            <div className="text-[10px] font-bold tracking-[0.3em] uppercase text-white/50 text-center mb-4">
              {t("login_with")}
            </div>
            <div className="flex flex-col gap-3 items-center">
              {!isRuDomain && (
                <button
                  type="button"
                  aria-label={t("continue_google")}
                  title={t("continue_google")}
                  data-testid="google-login-btn"
                  onClick={() => signInWith("google")}
                  className="w-[280px] h-[56px] bg-white text-black font-bold text-sm rounded-[12px] flex items-center justify-center gap-3 shadow-md hover:bg-neutral-100 transition-colors"
                >
                  <GoogleIcon size={24} />
                  <span>{t("continue_google")}</span>
                </button>
              )}
              
              <button
                type="button"
                aria-label={t("continue_yandex")}
                title={t("continue_yandex")}
                data-testid="yandex-login-btn"
                onClick={() => signInWith("custom:yandex")}
                className="w-[280px] h-[56px] bg-white text-black font-bold text-sm rounded-[12px] flex items-center justify-center gap-3 shadow-md hover:bg-neutral-100 transition-colors"
              >
                <YandexIcon size={24} />
                <span>{t("continue_yandex")}</span>
              </button>
            </div>
          </div>

          <footer className="mt-12 pt-8 border-t border-white/10 text-center">
            <Link
              to="/privacy"
              className="text-[10px] font-bold tracking-[0.2em] uppercase text-white/40 hover:text-white/70 transition-colors"
            >
              {t("privacy_policy")}
            </Link>
          </footer>
        </div>
      </div>
    </div>
  );
}
